(() => {
  class CTWishlistPage {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector(
        '[data-ct-wishlist-grid]'
      );
      this.loading = section.querySelector(
        '[data-ct-wishlist-loading]'
      );
      this.empty = section.querySelector(
        '[data-ct-wishlist-empty]'
      );
      this.error = section.querySelector(
        '[data-ct-wishlist-error]'
      );
      this.status = section.querySelector(
        '[data-ct-wishlist-status]'
      );
      this.clearButton = section.querySelector(
        '[data-ct-wishlist-clear]'
      );
      this.retryButton = section.querySelector(
        '[data-ct-wishlist-retry]'
      );

      this.storageKey =
        section.dataset.storageKey ||
        'ct_wishlist_v1';

      this.currency =
        window.Shopify?.currency?.active ||
        section.dataset.currency ||
        'USD';

      this.labels = {
        add:
          section.dataset.addLabel ||
          'Add to cart',
        adding:
          section.dataset.addingLabel ||
          'Adding...',
        added:
          section.dataset.addedLabel ||
          'Added to cart',
        out:
          section.dataset.outLabel ||
          'Out of stock',
        view:
          section.dataset.viewLabel ||
          'View product',
        remove:
          section.dataset.removeLabel ||
          'Remove from wishlist',
        unavailable:
          section.dataset.unavailableLabel ||
          'Product unavailable',
      };

      this.renderTimer = null;
      this.renderVersion = 0;

      if (!this.grid) {
        return;
      }

      this.bindEvents();
      this.render();
    }

    getRoot() {
      return (
        window.Shopify?.routes?.root ||
        '/'
      );
    }

    getHandles() {
      if (
        window.CTWishlist?.getHandles
      ) {
        return window.CTWishlist.getHandles();
      }

      try {
        const storedValue =
          window.localStorage.getItem(
            this.storageKey
          );

        const parsedValue = storedValue
          ? JSON.parse(storedValue)
          : [];

        return Array.isArray(parsedValue)
          ? [...new Set(parsedValue)]
          : [];
      } catch (error) {
        console.warn(
          'Chubbytrend Wishlist Page: Could not read Local Storage.',
          error
        );

        return [];
      }
    }

    writeHandles(handles) {
      const cleanHandles = [
        ...new Set(
          handles.filter(
            (handle) =>
              typeof handle === 'string' &&
              handle.trim() !== ''
          )
        ),
      ];

      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify(cleanHandles)
      );

      document.dispatchEvent(
        new CustomEvent(
          'ct:wishlist:updated',
          {
            detail: {
              handles: cleanHandles,
              count:
                cleanHandles.length,
            },
          }
        )
      );
    }

    removeHandle(handle) {
      if (
        window.CTWishlist?.remove
      ) {
        window.CTWishlist.remove(
          handle
        );

        return;
      }

      this.writeHandles(
        this.getHandles().filter(
          (savedHandle) =>
            savedHandle !== handle
        )
      );
    }

    clearWishlist() {
      if (
        window.CTWishlist?.clear
      ) {
        window.CTWishlist.clear();

        return;
      }

      this.writeHandles([]);
    }

    bindEvents() {
      this.grid.addEventListener(
        'click',
        (event) => {
          const removeButton =
            event.target.closest(
              '[data-ct-wishlist-remove]'
            );

          if (removeButton) {
            this.handleRemove(
              removeButton
            );

            return;
          }

          const addButton =
            event.target.closest(
              '[data-ct-wishlist-add]'
            );

          if (addButton) {
            this.handleAddToCart(
              addButton
            );
          }
        }
      );

      this.clearButton?.addEventListener(
        'click',
        () => {
          const shouldClear =
            window.confirm(
              'Remove all products from your wishlist?'
            );

          if (!shouldClear) {
            return;
          }

          this.clearWishlist();

          /*
           * The clear operation dispatches
           * ct:wishlist:updated. That event
           * schedules the authoritative render.
           */
        }
      );

      this.retryButton?.addEventListener(
        'click',
        () => {
          this.scheduleRender();
        }
      );

      document.addEventListener(
        'ct:wishlist:updated',
        () => {
          this.scheduleRender();
        }
      );

      window.addEventListener(
        'storage',
        (event) => {
          if (
            event.key ===
            this.storageKey
          ) {
            this.scheduleRender();
          }
        }
      );
    }

    scheduleRender() {
      /*
       * Invalidate every older asynchronous
       * rendering operation immediately.
       *
       * This prevents an old product request
       * from rendering an item after the
       * Wishlist has already changed.
       */
      this.renderVersion += 1;

      const scheduledVersion =
        this.renderVersion;

      window.clearTimeout(
        this.renderTimer
      );

      this.renderTimer =
        window.setTimeout(() => {
          if (
            scheduledVersion !==
            this.renderVersion
          ) {
            return;
          }

          this.render(
            scheduledVersion
          );
        }, 80);
    }

    handleListsMatch(
      first,
      second
    ) {
      if (
        first.length !==
        second.length
      ) {
        return false;
      }

      return first.every(
        (handle, index) =>
          handle === second[index]
      );
    }

    setLoading(isLoading) {
      if (this.loading) {
        this.loading.hidden =
          !isLoading;
      }

      if (isLoading) {
        this.grid.replaceChildren();
      }
    }

    hideStates() {
      if (this.empty) {
        this.empty.hidden = true;
      }

      if (this.error) {
        this.error.hidden = true;
      }
    }

    showEmpty() {
      this.setLoading(false);
      this.grid.replaceChildren();

      if (this.empty) {
        this.empty.hidden = false;
      }

      if (this.error) {
        this.error.hidden = true;
      }

      if (this.clearButton) {
        this.clearButton.hidden =
          true;
      }

      if (this.status) {
        this.status.textContent =
          '';
      }
    }

    showError() {
      this.setLoading(false);

      if (this.error) {
        this.error.hidden = false;
      }

      if (this.empty) {
        this.empty.hidden = true;
      }

      if (this.status) {
        this.status.textContent =
          '';
      }
    }

    async fetchProduct(handle) {
      const root =
        this.getRoot();

      const response =
        await fetch(
          `${root}products/${encodeURIComponent(
            handle
          )}.js`,
          {
            headers: {
              Accept:
                'application/json',
            },
          }
        );

      if (!response.ok) {
        throw new Error(
          `Could not load product: ${handle}`
        );
      }

      return response.json();
    }

    async fetchProducts(handles) {
      const results =
        new Map();

      const batchSize = 6;

      for (
        let batchStart = 0;
        batchStart <
        handles.length;
        batchStart += batchSize
      ) {
        const batch =
          handles.slice(
            batchStart,
            batchStart +
              batchSize
          );

        const batchResults =
          await Promise.allSettled(
            batch.map(
              (handle) =>
                this.fetchProduct(
                  handle
                )
            )
          );

        batchResults.forEach(
          (result, index) => {
            const handle =
              batch[index];

            if (
              result.status ===
              'fulfilled'
            ) {
              results.set(
                handle,
                {
                  product:
                    result.value,
                  error: null,
                }
              );
            } else {
              results.set(
                handle,
                {
                  product: null,
                  error:
                    result.reason,
                }
              );
            }
          }
        );
      }

      return results;
    }

    async render(
      requestedVersion = null
    ) {
      const renderVersion =
        requestedVersion === null
          ? ++this.renderVersion
          : requestedVersion;

      if (
        renderVersion !==
        this.renderVersion
      ) {
        return;
      }

      const handles =
        this.getHandles();

      this.hideStates();

      if (
        handles.length === 0
      ) {
        this.showEmpty();

        return;
      }

      if (this.clearButton) {
        this.clearButton.hidden =
          false;
      }

      this.setLoading(true);

      if (this.status) {
        this.status.textContent =
          handles.length === 1
            ? '1 saved product'
            : `${handles.length} saved products`;
      }

      try {
        const results =
          await this.fetchProducts(
            handles
          );

        /*
         * The Wishlist changed while product
         * data was loading. Ignore this
         * outdated response.
         */
        if (
          renderVersion !==
          this.renderVersion
        ) {
          return;
        }

        const latestHandles =
          this.getHandles();

        /*
         * Even if the render version somehow
         * remains unchanged, verify that the
         * Wishlist used to start this request
         * still matches the latest Wishlist.
         */
        if (
          !this.handleListsMatch(
            handles,
            latestHandles
          )
        ) {
          this.scheduleRender();

          return;
        }

        this.grid.replaceChildren();

        handles.forEach(
          (handle) => {
            const result =
              results.get(handle);

            if (
              result?.product
            ) {
              this.grid.append(
                this.createProductCard(
                  result.product,
                  handle
                )
              );
            } else {
              this.grid.append(
                this.createUnavailableCard(
                  handle
                )
              );
            }
          }
        );

        this.setLoading(false);
      } catch (error) {
        if (
          renderVersion !==
          this.renderVersion
        ) {
          return;
        }

        console.error(
          'Chubbytrend Wishlist:',
          error
        );

        this.showError();
      }
    }

    getProductImage(product) {
      if (
        typeof product.featured_image ===
        'string'
      ) {
        return product.featured_image;
      }

      if (
        product.featured_image
          ?.src
      ) {
        return product.featured_image
          .src;
      }

      if (
        Array.isArray(
          product.images
        )
      ) {
        const firstImage =
          product.images[0];

        return typeof firstImage ===
          'string'
          ? firstImage
          : firstImage?.src ||
              '';
      }

      return '';
    }

    getProductUrl(
      product,
      handle
    ) {
      if (product.url) {
        return product.url;
      }

      return `${this.getRoot()}products/${encodeURIComponent(
        handle
      )}`;
    }

    getSelectedVariant(product) {
      if (
        !Array.isArray(
          product.variants
        )
      ) {
        return null;
      }

      return (
        product.variants.find(
          (variant) =>
            variant.available
        ) ||
        product.variants[0] ||
        null
      );
    }

    formatMoney(cents) {
      const numericCents =
        Number(cents);

      if (
        !Number.isFinite(
          numericCents
        )
      ) {
        return '';
      }

      try {
        return new Intl.NumberFormat(
          document.documentElement
            .lang || 'en',
          {
            style: 'currency',
            currency:
              this.currency,
          }
        ).format(
          numericCents / 100
        );
      } catch (error) {
        return `${(
          numericCents / 100
        ).toFixed(2)} ${
          this.currency
        }`;
      }
    }

    createElement(
      tag,
      className,
      text
    ) {
      const element =
        document.createElement(
          tag
        );

      if (className) {
        element.className =
          className;
      }

      if (
        text !== undefined &&
        text !== null
      ) {
        element.textContent =
          text;
      }

      return element;
    }

    createProductCard(
      product,
      handle
    ) {
      const variant =
        this.getSelectedVariant(
          product
        );

      const isAvailable =
        Boolean(
          variant &&
            variant.available
        );

      const productUrl =
        this.getProductUrl(
          product,
          handle
        );

      const card =
        this.createElement(
          'article',
          'ct-wishlist-page__card'
        );

      card.dataset.productHandle =
        handle;

      card.setAttribute(
        'role',
        'listitem'
      );

      const mediaLink =
        this.createElement(
          'a',
          'ct-wishlist-page__media'
        );

      mediaLink.href =
        productUrl;

      const imageUrl =
        this.getProductImage(
          product
        );

      if (imageUrl) {
        const image =
          this.createElement(
            'img',
            'ct-wishlist-page__image'
          );

        image.src = imageUrl;
        image.alt =
          product.title || '';
        image.loading = 'lazy';
        image.width = 700;
        image.height = 700;

        mediaLink.append(
          image
        );
      }

      const removeButton =
        this.createElement(
          'button',
          'ct-wishlist-page__remove'
        );

      removeButton.type =
        'button';

      removeButton.dataset
        .ctWishlistRemove = '';

      removeButton.dataset
        .productHandle =
        handle;

      removeButton.setAttribute(
        'aria-label',
        `${this.labels.remove}: ${
          product.title
        }`
      );

      removeButton.innerHTML = `
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M12 21s-7.2-4.35-9.45-8.31C.75 9.52 2.08 5.5 5.72 4.55c2.06-.54 4.18.27 5.45 1.93L12 7.56l.83-1.08c1.27-1.66 3.39-2.47 5.45-1.93 3.64.95 4.97 4.97 3.17 8.14C19.2 16.65 12 21 12 21Z"
            fill="currentColor"
          />
        </svg>
      `;

      card.append(
        mediaLink,
        removeButton
      );

      const content =
        this.createElement(
          'div',
          'ct-wishlist-page__content'
        );

      const title =
        this.createElement(
          'h2',
          'ct-wishlist-page__title'
        );

      const titleLink =
        this.createElement(
          'a',
          'ct-wishlist-page__title-link',
          product.title
        );

      titleLink.href =
        productUrl;

      title.append(
        titleLink
      );

      const price =
        this.createElement(
          'div',
          'ct-wishlist-page__price'
        );

      if (variant) {
        const currentPrice =
          this.createElement(
            'span',
            'ct-wishlist-page__current-price',
            this.formatMoney(
              variant.price
            )
          );

        price.append(
          currentPrice
        );

        if (
          Number(
            variant.compare_at_price
          ) >
          Number(
            variant.price
          )
        ) {
          const comparePrice =
            this.createElement(
              'span',
              'ct-wishlist-page__compare-price',
              this.formatMoney(
                variant.compare_at_price
              )
            );

          price.append(
            comparePrice
          );
        }
      }

      const availability =
        this.createElement(
          'p',
          'ct-wishlist-page__availability',
          isAvailable
            ? 'In stock'
            : this.labels.out
        );

      availability.classList.toggle(
        'is-available',
        isAvailable
      );

      const actions =
        this.createElement(
          'div',
          'ct-wishlist-page__actions'
        );

      const addButton =
        this.createElement(
          'button',
          'ct-wishlist-page__add',
          isAvailable
            ? this.labels.add
            : this.labels.out
        );

      addButton.type =
        'button';

      addButton.disabled =
        !isAvailable;

      addButton.dataset
        .ctWishlistAdd = '';

      addButton.dataset.variantId =
        variant?.id || '';

      addButton.dataset
        .defaultLabel =
        this.labels.add;

      const viewLink =
        this.createElement(
          'a',
          'ct-wishlist-page__view',
          this.labels.view
        );

      viewLink.href =
        productUrl;

      actions.append(
        addButton,
        viewLink
      );

      content.append(
        title,
        price,
        availability,
        actions
      );

      card.append(
        content
      );

      return card;
    }

    createUnavailableCard(
      handle
    ) {
      const card =
        this.createElement(
          'article',
          'ct-wishlist-page__card'
        );

      card.dataset.productHandle =
        handle;

      card.setAttribute(
        'role',
        'listitem'
      );

      const content =
        this.createElement(
          'div',
          'ct-wishlist-page__content'
        );

      const title =
        this.createElement(
          'h2',
          'ct-wishlist-page__title',
          this.labels.unavailable
        );

      const removeButton =
        this.createElement(
          'button',
          'ct-wishlist-page__add',
          this.labels.remove
        );

      removeButton.type =
        'button';

      removeButton.dataset
        .ctWishlistRemove = '';

      removeButton.dataset
        .productHandle =
        handle;

      content.append(
        title,
        removeButton
      );

      card.append(
        content
      );

      return card;
    }

    handleRemove(button) {
      const handle =
        button.dataset
          .productHandle;

      if (!handle) {
        return;
      }

      const card =
        button.closest(
          '.ct-wishlist-page__card'
        );

      card?.classList.add(
        'is-removing'
      );

      window.setTimeout(() => {
        this.removeHandle(
          handle
        );

        /*
         * Do not call render() directly here.
         * removeHandle() dispatches
         * ct:wishlist:updated, and that event
         * schedules one authoritative render.
         */
      }, 180);
    }

    async handleAddToCart(
      button
    ) {
      const variantId =
        Number(
          button.dataset.variantId
        );

      if (
        !variantId ||
        button.disabled
      ) {
        return;
      }

      const defaultLabel =
        button.dataset
          .defaultLabel ||
        this.labels.add;

      button.disabled = true;
      button.textContent =
        this.labels.adding;

      try {
        const response =
          await fetch(
            `${this.getRoot()}cart/add.js`,
            {
              method: 'POST',
              headers: {
                Accept:
                  'application/json',
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify({
                  items: [
                    {
                      id:
                        variantId,
                      quantity: 1,
                    },
                  ],
                }),
            }
          );

        const responseData =
          await response.json();

        if (!response.ok) {
          throw new Error(
            responseData.description ||
              'Could not add product to cart.'
          );
        }

        button.textContent =
          this.labels.added;

        document.dispatchEvent(
          new CustomEvent(
            'ct:cart:updated',
            {
              detail:
                responseData,
            }
          )
        );

        await this.refreshCartState();

        window.setTimeout(() => {
          button.disabled =
            false;

          button.textContent =
            defaultLabel;
        }, 1600);
      } catch (error) {
        console.error(
          'Chubbytrend Wishlist cart:',
          error
        );

        button.disabled = false;
        button.textContent =
          defaultLabel;
      }
    }

    async refreshCartState() {
      try {
        const response =
          await fetch(
            `${this.getRoot()}cart.js`,
            {
              headers: {
                Accept:
                  'application/json',
              },
            }
          );

        if (!response.ok) {
          return;
        }

        const cart =
          await response.json();

        document.dispatchEvent(
          new CustomEvent(
            'ct:cart:state',
            {
              detail: cart,
            }
          )
        );

        document
          .querySelectorAll(
            '[data-ct-cart-count]'
          )
          .forEach(
            (counter) => {
              counter.textContent =
                cart.item_count;
            }
          );
      } catch (error) {
        console.warn(
          'Could not refresh cart count.',
          error
        );
      }
    }
  }

  function initializeCTWishlistPages(
    root = document
  ) {
    root
      .querySelectorAll(
        '[data-ct-wishlist-page]:not([data-ct-wishlist-page-initialized])'
      )
      .forEach((section) => {
        section.setAttribute(
          'data-ct-wishlist-page-initialized',
          'true'
        );

        new CTWishlistPage(
          section
        );
      });
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeCTWishlistPages();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCTWishlistPages(
        event.target
      );
    }
  );
})();